import React, { ComponentProps, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Content, useContent } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button } from 'src/core/ui';
import { SaleEventBox } from 'src/dialogs/LinkMarketplacePaymentLineDialog/SaleEventBox/SaleEventBox';
import { useLinkMarketplacePaymentLineToSale } from 'src/hooks/api/useLinkMarketplacePaymentLineToSale';
import { FieldWrapper } from 'src/modals/common';
import { ContentId } from 'src/utils/constants/contentId';
import { Sale } from 'src/WebApiController';

export type LinkMarketplacePaymentLineToSaleDialogProps = Omit<
  ComponentProps<typeof GenericDialog>,
  'header' | 'footer'
> & {
  onCancel: () => void;
  marketplacePaymentLineId: number | undefined;
  onLinkSuccess: () => void;
};

type LinkMarketplacePaymentLineToSaleForm = {
  lookupId: string | null; // can be either marketplace sale id or pos sale id
  saleId: number | null; // pos sale id
  marketplacePaymentLineId: number;
};

export const LinkMarketplacePaymentLineToSaleDialog = ({
  onCancel,
  payment,
  marketplacePaymentLineId,
  closeDialog,
  onLinkSuccess,
  ...dialogProps
}: LinkMarketplacePaymentLineToSaleDialogProps) => {
  const requiredMsg = useContent(ContentId.Required);
  const [existsSaleId, setExistsSaleId] = useState<boolean>(false);
  const [posSaleIdToLink, setPosSaleIdToLink] = useState<number>();

  const { register, formState, handleSubmit, trigger, getValues, watch } =
    useForm<LinkMarketplacePaymentLineToSaleForm>({
      defaultValues: {
        lookupId: null,
        saleId: null,
        marketplacePaymentLineId,
      },
    });

  const { mutateAsync, isPending } = useLinkMarketplacePaymentLineToSale();

  const lookupId = watch('lookupId');

  const onSubmit = useCallback(async () => {
    const hasErrors = Object.keys(formState.errors).length > 0;
    if (hasErrors || !existsSaleId) {
      return;
    }
    const { marketplacePaymentLineId } = getValues();

    await mutateAsync({
      saleId: posSaleIdToLink ?? undefined,
      marketplacePaymentLineId,
    });
    closeDialog();
    onLinkSuccess();
  }, [
    closeDialog,
    existsSaleId,
    formState.errors,
    getValues,
    mutateAsync,
    onLinkSuccess,
  ]);

  const onSearchSaleChanged = useCallback(
    (saleDetails: Sale | undefined | null) => {
      setExistsSaleId(!!saleDetails);
      setPosSaleIdToLink(saleDetails?.id);
    },
    []
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <GenericDialog
        {...dialogProps}
        size="lg"
        header={<Content id={ContentId.LinkSale} />}
        footer={
          <Button
            disabled={!existsSaleId}
            onClick={async () => {
              await trigger();
              await handleSubmit(onSubmit)();
            }}
          >
            <Content id={ContentId.Link} />
          </Button>
        }
        onCancel={onCancel}
      >
        {isPending ? (
          <PosSpinner />
        ) : (
          <>
            <FieldWrapper>
              <PosFormField
                label={<Content id={ContentId.SaleId} />}
                errors={formState.errors.saleId?.message}
              >
                <PosTextField
                  {...register('lookupId', { required: requiredMsg })}
                />
              </PosFormField>
              <input hidden type="submit" />
            </FieldWrapper>

            {!!lookupId && (
              <SaleEventBox
                lookupId={lookupId}
                onSearchSaleChanged={onSearchSaleChanged}
              />
            )}
          </>
        )}
      </GenericDialog>
    </form>
  );
};
